import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Plus, 
  Search, 
  Star,
  Package,
  Sparkles,
  ShoppingCart,
  ChevronRight,
  Share2,
  MessageCircle,
  ExternalLink
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate WhatsApp share link for a product
const getWhatsAppShareLink = (product) => {
  const productUrl = product.shopify_handle 
    ? `https://hearclear.in/products/${product.shopify_handle}`
    : `https://hearclear.in/collections/hearing-aid-types`;
  
  const message = `Check out ${product.name} - ₹${product.price?.toLocaleString()}
    
${product.description?.slice(0, 100)}...

View product: ${productUrl}

For more info, visit HearClear.in or call 9888438843`;
  
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
};

export default function ProductsPage({ user, logout }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterPriceBand, setFilterPriceBand] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [recommendLoading, setRecommendLoading] = useState(false);
  const [recommendForm, setRecommendForm] = useState({
    hearing_level: "",
    lifestyle: "",
    budget: "",
    cosmetic_preference: "",
    tech_comfort: "",
    notes: ""
  });
  const [newProduct, setNewProduct] = useState({
    name: "",
    category: "Behind-the-Ear",
    description: "",
    story: "",
    ideal_user: "",
    lifestyle_fit: "",
    benefits: [],
    price: 0,
    price_band: "mid",
    warranty: "2 years",
    images: []
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [filterCategory, filterPriceBand, searchQuery]);

  const fetchProducts = async () => {
    try {
      let url = `${API}/products?limit=100`;
      if (filterCategory) url += `&category=${encodeURIComponent(filterCategory)}`;
      if (filterPriceBand) url += `&price_band=${filterPriceBand}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await axios.get(url);
      setProducts(response.data.products || []);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/products/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Failed to load categories");
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.name || !newProduct.price) {
      toast.error("Name and price are required");
      return;
    }
    try {
      await axios.post(`${API}/products`, newProduct, { withCredentials: true });
      toast.success("Product created successfully");
      setShowAddModal(false);
      fetchProducts();
    } catch (error) {
      toast.error("Failed to create product");
    }
  };

  const getAIRecommendations = async () => {
    setRecommendLoading(true);
    try {
      const response = await axios.post(`${API}/ai/recommendations`, recommendForm, { withCredentials: true });
      setRecommendations(response.data);
    } catch (error) {
      toast.error("Failed to get AI recommendations");
    } finally {
      setRecommendLoading(false);
    }
  };

  const getPriceBandColor = (band) => {
    const colors = {
      economy: "bg-green-100 text-green-800",
      mid: "bg-blue-100 text-blue-800",
      premium: "bg-purple-100 text-purple-800"
    };
    return colors[band] || "bg-slate-100 text-slate-800";
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="products-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Products</h1>
            <p className="text-slate-600 mt-1">Manage your hearing aid catalog</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={() => setShowRecommendModal(true)}
              className="border-cyan-700 text-cyan-700 hover:bg-cyan-50"
              data-testid="ai-recommend-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              AI Recommendations
            </Button>
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
              <DialogTrigger asChild>
                <Button className="bg-cyan-700 hover:bg-cyan-800" data-testid="add-product-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Product</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateProduct} className="space-y-4">
                  <div>
                    <label className="form-label">Name *</label>
                    <Input
                      placeholder="Product name"
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Category</label>
                      <Select 
                        value={newProduct.category} 
                        onValueChange={(value) => setNewProduct({ ...newProduct, category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Behind-the-Ear">Behind-the-Ear</SelectItem>
                          <SelectItem value="In-the-Ear">In-the-Ear</SelectItem>
                          <SelectItem value="Receiver-in-Canal">Receiver-in-Canal</SelectItem>
                          <SelectItem value="Completely-in-Canal">Completely-in-Canal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="form-label">Price Band</label>
                      <Select 
                        value={newProduct.price_band} 
                        onValueChange={(value) => setNewProduct({ ...newProduct, price_band: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="economy">Economy</SelectItem>
                          <SelectItem value="mid">Mid-Range</SelectItem>
                          <SelectItem value="premium">Premium</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Price (₹) *</label>
                    <Input
                      type="number"
                      placeholder="25000"
                      value={newProduct.price}
                      onChange={(e) => setNewProduct({ ...newProduct, price: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="form-label">Description</label>
                    <Textarea
                      placeholder="Product description..."
                      value={newProduct.description}
                      onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label">Story (Marketing)</label>
                    <Textarea
                      placeholder="Tell the product's story..."
                      value={newProduct.story}
                      onChange={(e) => setNewProduct({ ...newProduct, story: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-cyan-700 hover:bg-cyan-800">
                      Create Product
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search products..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-products-input"
                />
              </div>
              <Select value={filterCategory || "all"} onValueChange={(val) => setFilterCategory(val === "all" ? "" : val)}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterPriceBand || "all"} onValueChange={(val) => setFilterPriceBand(val === "all" ? "" : val)}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="All Price Bands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Price Bands</SelectItem>
                  <SelectItem value="economy">Economy</SelectItem>
                  <SelectItem value="mid">Mid-Range</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : products.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900">No products found</h3>
              <p className="text-slate-500 mt-1">Add your first product to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card 
                key={product.product_id} 
                className="overflow-hidden card-hover cursor-pointer"
                onClick={() => {
                  setSelectedProduct(product);
                  setShowDetailModal(true);
                }}
                data-testid={`product-card-${product.product_id}`}
              >
                <div className="aspect-video bg-slate-100 relative overflow-hidden">
                  {product.images?.[0] ? (
                    <img 
                      src={product.images[0]} 
                      alt={product.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  <Badge className={`absolute top-3 right-3 ${getPriceBandColor(product.price_band)}`}>
                    {product.price_band}
                  </Badge>
                </div>
                <CardContent className="p-5">
                  <p className="text-sm text-cyan-700 font-medium">{product.category}</p>
                  <h3 className="text-lg font-semibold text-slate-900 mt-1 font-[Manrope]">{product.name}</h3>
                  <p className="text-sm text-slate-500 mt-2 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-xl font-bold text-slate-900">₹{product.price?.toLocaleString()}</p>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(getWhatsAppShareLink(product), '_blank');
                        }}
                        data-testid={`share-whatsapp-${product.product_id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-cyan-700">
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Product Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedProduct && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-xl">{selectedProduct.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-6 mt-4">
                  {/* Image */}
                  <div className="aspect-video bg-slate-100 rounded-xl overflow-hidden">
                    {selectedProduct.images?.[0] ? (
                      <img 
                        src={selectedProduct.images[0]} 
                        alt={selectedProduct.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-16 h-16 text-slate-300" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex items-center gap-4">
                    <Badge className={getPriceBandColor(selectedProduct.price_band)}>
                      {selectedProduct.price_band}
                    </Badge>
                    <Badge variant="outline">{selectedProduct.category}</Badge>
                    <Badge variant="outline">{selectedProduct.warranty} warranty</Badge>
                  </div>

                  <div>
                    <p className="text-3xl font-bold text-slate-900">₹{selectedProduct.price?.toLocaleString()}</p>
                  </div>

                  {/* Story */}
                  {selectedProduct.story && (
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">The Story</h4>
                      <p className="text-slate-600 italic">{selectedProduct.story}</p>
                    </div>
                  )}

                  {/* Description */}
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Description</h4>
                    <p className="text-slate-600">{selectedProduct.description}</p>
                  </div>

                  {/* Ideal User */}
                  {selectedProduct.ideal_user && (
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Ideal For</h4>
                      <p className="text-slate-600">{selectedProduct.ideal_user}</p>
                    </div>
                  )}

                  {/* Benefits */}
                  {selectedProduct.benefits?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Benefits</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProduct.benefits.map((benefit, index) => (
                          <Badge key={index} variant="secondary" className="bg-teal-50 text-cyan-700">
                            {benefit}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-col gap-3 pt-4 border-t">
                    <div className="flex gap-3">
                      <Button 
                        variant="outline" 
                        className="flex-1 text-green-600 border-green-600 hover:bg-green-50"
                        onClick={() => window.open(getWhatsAppShareLink(selectedProduct), '_blank')}
                        data-testid="share-whatsapp-modal-btn"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Share on WhatsApp
                      </Button>
                      {selectedProduct.shopify_handle && (
                        <Button 
                          className="flex-1 bg-orange-500 hover:bg-orange-600"
                          onClick={() => window.open(`https://hearclear.in/products/${selectedProduct.shopify_handle}`, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View in Shop
                        </Button>
                      )}
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => setShowDetailModal(false)}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* AI Recommendations Modal */}
        <Dialog open={showRecommendModal} onOpenChange={setShowRecommendModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-700" />
                AI Product Recommendations
              </DialogTitle>
            </DialogHeader>
            
            {!recommendations ? (
              <div className="space-y-4 mt-4">
                <p className="text-slate-600">Enter customer details to get personalized product recommendations</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Hearing Level</label>
                    <Select 
                      value={recommendForm.hearing_level} 
                      onValueChange={(value) => setRecommendForm({ ...recommendForm, hearing_level: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mild">Mild</SelectItem>
                        <SelectItem value="moderate">Moderate</SelectItem>
                        <SelectItem value="severe">Severe</SelectItem>
                        <SelectItem value="profound">Profound</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="form-label">Lifestyle</label>
                    <Select 
                      value={recommendForm.lifestyle} 
                      onValueChange={(value) => setRecommendForm({ ...recommendForm, lifestyle: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select lifestyle" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active/Social</SelectItem>
                        <SelectItem value="moderate">Moderate Activity</SelectItem>
                        <SelectItem value="home">Home-based</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="form-label">Budget</label>
                    <Select 
                      value={recommendForm.budget} 
                      onValueChange={(value) => setRecommendForm({ ...recommendForm, budget: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select budget" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="economy">Under ₹30,000</SelectItem>
                        <SelectItem value="mid">₹30,000 - ₹60,000</SelectItem>
                        <SelectItem value="premium">₹60,000+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="form-label">Tech Comfort</label>
                    <Select 
                      value={recommendForm.tech_comfort} 
                      onValueChange={(value) => setRecommendForm({ ...recommendForm, tech_comfort: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select comfort" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">Tech-savvy</SelectItem>
                        <SelectItem value="moderate">Some experience</SelectItem>
                        <SelectItem value="low">Prefer simple</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="form-label">Additional Notes</label>
                  <Textarea
                    placeholder="Any other preferences or requirements..."
                    value={recommendForm.notes}
                    onChange={(e) => setRecommendForm({ ...recommendForm, notes: e.target.value })}
                  />
                </div>
                <Button 
                  onClick={getAIRecommendations}
                  className="w-full bg-cyan-700 hover:bg-cyan-800"
                  disabled={recommendLoading}
                  data-testid="get-recommendations-btn"
                >
                  {recommendLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Get AI Recommendations
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-6 mt-4">
                {/* Overall Advice */}
                {recommendations.overall_advice && (
                  <div className="p-4 bg-teal-50 rounded-xl">
                    <p className="text-cyan-800">{recommendations.overall_advice}</p>
                  </div>
                )}

                {/* Recommendations */}
                <div className="space-y-4">
                  {recommendations.recommendations?.map((rec, index) => (
                    <Card key={index} className="border-l-4 border-l-cyan-700">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-slate-900">{rec.product_name}</h4>
                            <p className="text-sm text-slate-600 mt-1">{rec.reasoning}</p>
                            {rec.key_benefits && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                {rec.key_benefits.map((benefit, i) => (
                                  <Badge key={i} variant="secondary" className="bg-green-50 text-green-700">
                                    {benefit}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <Badge className="bg-orange-100 text-orange-800">#{index + 1}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Button 
                  variant="outline"
                  onClick={() => setRecommendations(null)}
                  className="w-full"
                >
                  Get New Recommendations
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
